// Shared right slide-over shell. Extracts the markup every hand-rolled
// drawer repeats (ActivateUnitDrawer, AssignBatchDrawer, ShareLinkDrawer
// and the facility AssignDrawer): backdrop + <aside
// role="dialog"> with head / optional lead band / body / optional foot,
// focus trap + restore and Escape-to-close through useDialogFocus.
//
// Portaled to document.body by default for the same reason
// ActivateUnitDrawer is: a trigger that lives inside a <td> would
// otherwise have its drawer hoisted out of the table by the parser and
// the focus/Escape handling would land on a detached node. `portal`
// stays overridable for the drawers whose tests query `container`.
//
// Módulo Administración (plan §1.4): `wide` da la variante de 620px de
// los flujos de dos pasos (importar planilla, invitar en lote, subir
// documento) y `footerStart` pone la acción destructiva (Archivar,
// Eliminar) al borde izquierdo del pie, separada de Cancelar + primario.

import { useId, useRef } from "react";
import type { ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "../lib/useDialogFocus";
import { Icon } from "./icons";

export type DrawerProps = {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Rendered in `.drawer__foot`; the foot is omitted when absent. */
  footer?: ReactNode;
  /** Element to focus on open instead of the first focusable child. */
  initialFocusRef?: RefObject<HTMLElement>;
  /** Summary band between head and body (`.drawer__lead`) — the
   *  observation summary the v2 action drawers all carry. */
  lead?: ReactNode;
  /** createPortal to document.body (default). */
  portal?: boolean;
  /** Extra class on the <aside>, e.g. "drawer--hilo" for the wide variant. */
  className?: string;
  /** Variante ancha de Administración (620px, `ag-drawer--wide`). */
  wide?: boolean;
  /** Acción destructiva (`btn--danger`) alineada a la izquierda del pie;
   *  `footer` queda a la derecha. Sola, también abre el pie. */
  footerStart?: ReactNode;
};

export function Drawer({
  title,
  onClose,
  children,
  footer,
  initialFocusRef,
  lead,
  portal = true,
  className,
  wide = false,
  footerStart,
}: DrawerProps) {
  const titleId = useId();
  const drawerRef = useRef<HTMLElement>(null);

  useDialogFocus(true, drawerRef, {
    onEscape: onClose,
    ...(initialFocusRef ? { initialFocusRef } : {}),
  });

  const asideClass = ["drawer", wide ? "ag-drawer--wide" : null, className]
    .filter(Boolean)
    .join(" ");
  const hasFooter = footer !== undefined && footer !== null;
  const hasFooterStart = footerStart !== undefined && footerStart !== null;

  const node = (
    <>
      <div className="drawer-backdrop" onClick={onClose} role="presentation" />
      <aside
        ref={drawerRef}
        className={asideClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="drawer__head">
          <h2 id={titleId} className="drawer__title">
            {title}
          </h2>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Cerrar">
            <Icon.Close />
          </button>
        </div>
        {lead !== undefined && lead !== null && <div className="drawer__lead">{lead}</div>}
        <div className="drawer__body">{children}</div>
        {(hasFooter || hasFooterStart) && (
          <div className="drawer__foot">
            {hasFooterStart && <div className="ag-drawer__foot-start">{footerStart}</div>}
            {footer}
          </div>
        )}
      </aside>
    </>
  );

  return portal ? createPortal(node, document.body) : node;
}
